
import React from 'react';
import { MainScreen } from '../types';
import ThemeToggle from './ThemeToggle';
import Logo from './Logo';

interface NavigationProps {
  activeScreen: MainScreen;
  setActiveScreen: (screen: MainScreen) => void;
  theme: 'light' | 'dark';
  toggleTheme: () => void;
}

const Navigation: React.FC<NavigationProps> = ({ activeScreen, setActiveScreen, theme, toggleTheme }) => {
  const items = [
    { screen: MainScreen.DASHBOARD, label: 'Visão Geral', icon: 'fa-house' },
    { screen: MainScreen.TRANSACTIONS, label: 'Lançamentos', icon: 'fa-list-ul' },
    { screen: MainScreen.CARDS, label: 'Cartões', icon: 'fa-credit-card' },
    { screen: MainScreen.WEALTH, label: 'Patrimônio', icon: 'fa-vault' },
    { screen: MainScreen.REPORTS, label: 'Relatórios', icon: 'fa-chart-pie' },
    { screen: MainScreen.PROFILE, label: 'Ajustes', icon: 'fa-gear' },
  ];

  return (
    <>
      {/* Mobile Bottom Nav */}
      <nav className="fixed bottom-0 left-0 right-0 bg-white/95 dark:bg-slate-900/95 backdrop-blur-md border-t border-slate-200 dark:border-slate-800 flex justify-around items-center px-1 pt-1.5 pb-[calc(0.4rem+env(safe-area-inset-bottom))] md:hidden z-50 shadow-[0_-10px_25px_-5px_rgba(0,0,0,0.1)] transition-all">
        {items.map((item) => (
          <button
            key={item.screen}
            onClick={() => setActiveScreen(item.screen)}
            className={`flex flex-col items-center justify-center flex-1 py-1 transition-all active:scale-90 ${
              activeScreen === item.screen ? 'text-blue-600' : 'text-slate-400 dark:text-slate-500'
            }`}
          >
            <i className={`fa-solid ${item.icon} text-lg mb-1`}></i>
            <span className={`text-[7px] font-black uppercase tracking-tighter leading-none text-center truncate px-0.5 ${activeScreen === item.screen ? 'opacity-100' : 'opacity-60'}`}>
              {item.label}
            </span>
          </button>
        ))}
      </nav>

      {/* Desktop Sidebar */}
      <nav className="hidden md:flex fixed left-0 top-0 bottom-0 w-64 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl border-r border-slate-200/50 dark:border-slate-800/50 flex-col p-6 z-50 transition-all">
        <div className="flex items-center justify-between mb-10 px-2">
          <div className="flex items-center gap-3 group cursor-pointer">
            <div className="relative">
              <div className="absolute -inset-1 bg-blue-500 rounded-xl blur opacity-25 group-hover:opacity-50 transition duration-1000 group-hover:duration-200"></div>
              <Logo size="md" className="relative" />
            </div>
            <span className="text-xl font-black heading-display tracking-tight text-slate-900 dark:text-white uppercase">Equilibra</span>
          </div>
        </div>
        
        <div className="space-y-1.5 flex-1 overflow-y-auto hide-scrollbar">
          <p className="text-[9px] font-black text-slate-400 dark:text-slate-600 uppercase tracking-[0.15em] mb-3 ml-3">Menu Principal</p>
          {items.map((item) => (
            <button
              key={item.screen}
              onClick={() => setActiveScreen(item.screen)}
              className={`flex items-center gap-3 w-full px-4 py-3 rounded-xl transition-all font-bold text-[10px] uppercase tracking-widest group ${
                activeScreen === item.screen 
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/25' 
                  : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-900/50 hover:text-slate-900 dark:hover:text-slate-100'
              }`}
            >
              <i className={`fa-solid ${item.icon} w-5 text-base text-center transition-transform group-hover:scale-110`}></i>
              <span className="truncate">{item.label}</span>
              {activeScreen === item.screen && (
                <div className="ml-auto w-1 h-1 rounded-full bg-white/50"></div>
              )}
            </button>
          ))}
          
          <div className="pt-6 mt-6 border-t border-slate-100 dark:border-slate-800/50">
            <p className="text-[9px] font-black text-slate-400 dark:text-slate-600 uppercase tracking-[0.15em] mb-3 ml-3">Premium</p>
            <button
              onClick={() => setActiveScreen(MainScreen.PROFILE)}
              className={`flex items-center gap-3 w-full px-5 py-4 rounded-xl transition-all font-black text-[10px] uppercase tracking-widest relative overflow-hidden group ${
                activeScreen === MainScreen.PROFILE 
                  ? 'bg-amber-500 text-white shadow-lg shadow-amber-500/20' 
                  : 'bg-slate-50 dark:bg-slate-900/50 text-amber-600 dark:text-amber-500 border border-amber-200/50 dark:border-amber-900/30 hover:bg-amber-50 dark:hover:bg-amber-900/20'
              }`}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:animate-[shimmer_2s_infinite]"></div>
              <i className="fa-solid fa-crown text-lg transition-transform group-hover:rotate-12"></i>
              <span>Plano de Acesso</span>
            </button>
          </div>
        </div>

        <div className="mt-auto pt-6 border-t border-slate-100 dark:border-slate-800/50">
           <div className="flex items-center justify-between px-2">
              <div className="flex flex-col">
                <p className="text-[9px] font-black text-slate-900 dark:text-white uppercase tracking-tight">Equilibra v1.2.9</p>
                <p className="text-[7px] font-bold text-slate-400 uppercase tracking-widest">Soberania de Dados</p>
              </div>
              <ThemeToggle theme={theme} toggleTheme={toggleTheme} className="scale-75" />
           </div>
        </div>
      </nav>
    </>
  );
};

export default Navigation;
